import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';
import type { Route } from '../lib/router';
import { useAtlas } from '../lib/store';
import { useSync } from '../lib/SyncProvider';
import { ConnectivityNotice } from './ConnectivityNotice';
import { IdentityDialog } from './IdentityDialog';
import {
  BookIcon,
  CloseIcon,
  CompassIcon,
  InfoIcon,
  MenuIcon,
  MoonIcon,
  NetworkIcon,
  NotebookIcon,
  SearchIcon,
  SparkIcon,
  SunIcon,
} from './Icons';

const SearchDialog = lazy(() => import('./SearchDialog').then((module) => ({ default: module.SearchDialog })));

const navItems = [
  { label: '探索', path: '/home', icon: NetworkIcon },
  { label: '案例', path: '/cases', icon: BookIcon },
  { label: '机会', path: '/opportunities', icon: SparkIcon },
  { label: '路径', path: '/trails', icon: CompassIcon },
  { label: '我的', path: '/notebook', icon: NotebookIcon },
];

const menuItems = [
  { number: '01', label: '案例库', detail: '按领域与关键词浏览', path: '/cases' },
  { number: '02', label: '机会雷达', detail: '从机制到可验证假设', path: '/opportunities' },
  { number: '03', label: '引导路径', detail: '沿着问题走一条因果链', path: '/trails' },
  { number: '04', label: '我的账本', detail: '收藏、看过、笔记与已掌握', path: '/notebook' },
  { number: '05', label: '方法与证据', detail: '五类知识、边界与交互规则', path: '/method' },
  { number: '06', label: '知识版图', detail: '覆盖方向、长期理论与审核记录', path: '/map' },
  { number: '07', label: '已看', detail: '真正浏览过的案例与知识点', path: '/viewed' },
];

function isActive(route: Route, path: string): boolean {
  if (path === '/home') {
    return route.segments[0] === 'home' || route.segments[0] === 'atlas' || route.segments[0] === 'lens' || route.segments.length === 0;
  }
  if (path === '/notebook') {
    return route.segments[0] === 'notebook' || route.segments[0] === 'viewed';
  }
  return route.segments[0] === path.slice(1);
}

function BrandMark() {
  return (
    <svg className="brand-mark" viewBox="0 0 40 40" aria-hidden="true">
      <circle cx="20" cy="20" r="16.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.38" />
      <circle cx="20" cy="20" r="10.5" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.6" />
      <path d="M20 3.5v8M36.5 20h-8M20 36.5v-8M3.5 20h8" stroke="currentColor" strokeWidth="1" opacity="0.7" />
      <path d="m8.5 8.5 6 6M31.5 8.5l-6 6M31.5 31.5l-6-6M8.5 31.5l6-6" stroke="currentColor" strokeWidth="1" opacity="0.45" />
      <circle cx="20" cy="20" r="3.2" fill="currentColor" />
      <circle cx="20" cy="3.5" r="1.6" fill="currentColor" />
      <circle cx="36.5" cy="20" r="1.6" fill="currentColor" />
      <circle cx="20" cy="36.5" r="1.6" fill="currentColor" />
      <circle cx="3.5" cy="20" r="1.6" fill="currentColor" />
    </svg>
  );
}

export function AppShell({ route, children }: { route: Route; children: ReactNode }) {
  const { theme, toggleTheme } = useAtlas();
  const { connected, openDialog } = useSync();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isHome = route.segments[0] === 'home' || route.segments.length === 0;
  const isNetwork = route.segments[0] === 'atlas';

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable;
      if ((event.key === '/' && !isTyping) || ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k')) {
        event.preventDefault();
        setSearchOpen(true);
      }
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <div className={`app-shell ${isHome ? 'is-home' : ''}`}>
      <header className={`site-header ${isHome ? 'home-header' : ''}`}>
        <a className="brand" href="#/home" aria-label="因果网首页">
          <BrandMark />
          <span className="brand-copy">
            <span className="brand-cn">因果网</span>
            <span className="brand-en">CAUSAL ATLAS</span>
          </span>
        </a>

        {isHome ? (
          <div className="home-nav-actions">
            <span className="home-nav-note">一次只看一个入口</span>
            <button className="icon-button" type="button" onClick={() => setSearchOpen(true)} aria-label="搜索">
              <SearchIcon size={19} />
            </button>
            <button className="icon-button" type="button" onClick={() => setMenuOpen(true)} aria-label="打开菜单">
              <MenuIcon size={19} />
            </button>
          </div>
        ) : (
          <>
            <nav className="desktop-nav" aria-label="主导航">
              {navItems.map((item) => (
                <a key={item.path} href={`#${item.path}`} className={isActive(route, item.path) ? 'nav-link is-active' : 'nav-link'}>
                  {item.label}
                </a>
              ))}
            </nav>

            <div className="header-actions">
              <button className="search-trigger" type="button" onClick={() => setSearchOpen(true)} aria-label="打开搜索">
                <SearchIcon size={17} />
                <span>搜索</span>
                <kbd>/</kbd>
              </button>
              <a href="#/method" className={isActive(route, '/method') ? 'icon-button is-active' : 'icon-button'} aria-label="阅读方法" title="方法与证据">
                <InfoIcon size={19} />
              </a>
              <button className="icon-button" type="button" onClick={toggleTheme} aria-label={theme === 'light' ? '切换到深色模式' : '切换到浅色模式'}>
                {theme === 'light' ? <MoonIcon size={18} /> : <SunIcon size={18} />}
              </button>
            </div>
          </>
        )}
      </header>

      {isHome && menuOpen ? (
        <div className="home-menu-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && setMenuOpen(false)}>
          <div className="home-menu" role="dialog" aria-modal="true" aria-label="站点菜单">
            <div className="home-menu-head">
              <div>
                <span className="eyebrow">CAUSAL ATLAS / INDEX</span>
                <h2>不只从一个入口看世界。</h2>
              </div>
              <button className="icon-button" type="button" onClick={() => setMenuOpen(false)} aria-label="关闭菜单">
                <CloseIcon size={19} />
              </button>
            </div>
            <nav className="home-menu-list">
              {menuItems.map((item) => (
                <a key={item.path} href={`#${item.path}`} onClick={() => setMenuOpen(false)}>
                  <span>{item.number}</span>
                  <div>
                    <strong>{item.label}</strong>
                    <small>{item.detail}</small>
                  </div>
                  <span className="home-menu-arrow">↗</span>
                </a>
              ))}
              <button
                className="home-menu-identity"
                type="button"
                onClick={() => {
                  setMenuOpen(false);
                  openDialog();
                }}
              >
                <span>08</span>
                <div>
                  <strong>个人身份</strong>
                  <small>{connected ? '已连接 · 查看跨设备同步' : '输入验证码，跨设备同步'}</small>
                </div>
                <span className="home-menu-arrow">↗</span>
              </button>
            </nav>
            <div className="home-menu-foot">
              <p>首页每次打开从探索队列随机选择一个案例。点击进入，左滑换一个，长按才出现操作。</p>
              <button className="button button-secondary" type="button" onClick={toggleTheme}>
                {theme === 'light' ? <MoonIcon size={15} /> : <SunIcon size={15} />}
                {theme === 'light' ? '切换到深色' : '切换到浅色'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConnectivityNotice />

      <main className={`site-main ${isHome ? 'is-home-main' : ''}`}>{children}</main>

      {!isHome && !isNetwork ? (
        <footer className="site-footer">
          <div className="footer-brand">
            <BrandMark />
            <div>
              <strong>因果网 Causal Atlas</strong>
              <p>从现实案例出发，拆开机制，再判断机会。</p>
            </div>
          </div>
          <div className="footer-note">
            <p>策展式知识产品 · 内容版本 2026.10</p>
            <p>案例用于说明机制，不构成投资、法律或经营建议。</p>
          </div>
          <div className="footer-links">
            <a href="#/method">方法与证据</a>
            <a href="#/map">知识版图</a>
            <a href="#/viewed">已看</a>
            <a href="#/cases">全部案例</a>
            <a href="#/notebook">我的账本</a>
          </div>
        </footer>
      ) : null}

      {!isHome ? (
        <nav className="mobile-nav" aria-label="移动端主导航">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <a key={item.path} href={`#${item.path}`} className={isActive(route, item.path) ? 'mobile-nav-link is-active' : 'mobile-nav-link'}>
                <Icon size={20} />
                <span>{item.label}</span>
              </a>
            );
          })}
        </nav>
      ) : null}

      {searchOpen ? (
        <Suspense fallback={null}>
          <SearchDialog open onClose={() => setSearchOpen(false)} />
        </Suspense>
      ) : null}
      <IdentityDialog />
    </div>
  );
}
